/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import React from 'react';
import { redirect, type RouteObject } from 'react-router-dom';

import {
  redirectToLandingIfPlanInaccessible,
  redirectToQuickstartIfPlanAccessible,
} from '@fai/friendli-suite/features/routes/loaders/product/container';
import ContainerLayout from '@fai/friendli-suite/views/layout/product/container/layout';
import { pipeLoaders } from '@fai/third-party/react-router-dom';
import { redirectIfSessionExpired } from '@fai/third-party/supertokens';

const LandingPage = React.lazy(() => import('@fai/friendli-suite/pages/container/landing'));
const QuickStartPage = React.lazy(() => import('@fai/friendli-suite/pages/container/quickstart'));
const RepositoryListPage = React.lazy(
  () => import('@fai/friendli-suite/pages/container/repository/repository-list')
);
const RepositoryDetailPage = React.lazy(
  () => import('@fai/friendli-suite/pages/container/repository/repository-detail')
);
const TagDetailPage = React.lazy(
  () => import('@fai/friendli-suite/pages/container/repository/tag-detail')
);
const SecretsPage = React.lazy(() => import('@fai/friendli-suite/pages/container/secrets'));

const containerRoutes: RouteObject[] = [
  {
    children: [
      {
        path: 'landing',
        loader: pipeLoaders({
          sequence: [redirectIfSessionExpired, redirectToQuickstartIfPlanAccessible],
        }),
        Component: LandingPage,
      },
      {
        Component: ContainerLayout,
        loader: pipeLoaders({
          sequence: [redirectIfSessionExpired, redirectToLandingIfPlanInaccessible],
        }),
        children: [
          {
            path: 'quickstart',
            Component: QuickStartPage,
          },
          {
            path: 'repository/*',
            children: [
              {
                path: 'list',
                Component: RepositoryListPage,
              },
              {
                path: ':repositoryId/*',
                children: [
                  {
                    path: 'detail',
                    Component: RepositoryDetailPage,
                  },
                  {
                    path: 'tag/:tagId',
                    Component: TagDetailPage,
                  },
                  {
                    path: '*',
                    loader: () => redirect('detail'),
                  },
                ],
              },
              {
                path: '*',
                loader: () => redirect('list'),
              },
            ],
          },
          {
            path: 'secrets',
            Component: SecretsPage,
          },
        ],
      },
      {
        path: '*',
        loader: () => redirect('landing'),
      },
    ],
  },
];

export default containerRoutes;
